import { Alert, Box, Skeleton, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { quoteLicence } from '../../api/admin/endpoints';
import type { LicenceOrderInput } from '../../api/admin/types';
import { InvoiceLines } from '../../components/InvoiceLines';
import { useDebouncedValue } from '../../components/useDebouncedValue';
import { useT } from '../../i18n/LanguageContext';

/** What an order would be invoiced at, from the price list, as the form is filled in. Writes nothing. */
export const QuotePreview = ({ order }: { order: LicenceOrderInput | null }) => {
  const t = useT();
  // Debounced as text: the order is rebuilt on every render, and a new object each time would never settle.
  const debounced = useDebouncedValue(order === null ? null : JSON.stringify(order));
  const query = useQuery({
    queryKey: ['quote', debounced],
    queryFn: () => quoteLicence(JSON.parse(debounced as string) as LicenceOrderInput),
    enabled: debounced !== null,
    retry: false,
  });

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {t('quote.title')}
      </Typography>
      {debounced === null ? null : query.isLoading ? (
        <Skeleton variant="rounded" height={120} />
      ) : query.data ? (
        <InvoiceLines
          lines={query.data.lines}
          netMinor={query.data.netMinor}
          vatMinor={query.data.vatMinor}
          totalMinor={query.data.totalMinor}
          vatPayer={query.data.vatPayer}
          vatRateBasisPoints={query.data.vatRateBasisPoints}
          currency="EUR"
        />
      ) : (
        <Alert severity="warning" variant="outlined">
          {t('quote.unavailable')}
        </Alert>
      )}
    </Box>
  );
};
